import { Select } from '@/components/Select';
import { WithLoader } from '@/components/WithLoader';
import {
    ContributionSeasonalityBarChart,
    ContributionSeasonalityData,
} from '@/dashboards/CampaignOptimization/components/ContributionSeasonality/ContributionSeasonalityBarChart';
import { OptimizedPieChart } from '@/dashboards/CampaignOptimization/components/ContributionSeasonality/OptimizedPieChart';
import { useOptimizationSession } from '@/dashboards/CampaignOptimization/hooks/useOptimizationSession';
import { useDashboardGlobalContext } from '@/dashboards/providers/dashboardGlobalContext';
import { groupContributionData } from '@/dashboards/utils/data';
import { structureMediaSpendSeasonality } from '@/dashboards/utils/data/structureMediaSpendSeasonality';
import { capitalizeFirstLetter } from '@/utils/stringUtils';
import { TabButtons, Typography } from '@analytical-alley/ui';
import React, { ReactNode, useMemo, useState } from 'react';

export const ContributionSeasonality = ({
    children,
}: {
    children: ReactNode;
}) => {
    const {
        colorMap: { variableColorMap, categoryColorMap },
        variableNameMap,
    } = useDashboardGlobalContext();
    const {
        data: {
            budgetByCategoryData,
            contributionLastYear: {
                data: contributionData,
                isFetching,
                isLoading,
            },
            optimized,
            previous,
        },
        totalContributionGraphPeriod,
        setTotalContributionGraphPeriod,
    } = useOptimizationSession();
    const [modelBreakdown, setModelBreakdown] = useState<
        'variable' | 'category'
    >('category');

    const selectedIndex = totalContributionGraphPeriod === 'monthly' ? 1 : 0;

    const modelContributionData = useMemo(() => {
        const mediasInRoi = Object.keys(
            previous?.roiCurves.variablesAndCategories.byVariable || {},
        );

        const rowsInRoi = contributionData?.rows.filter((row) => {
            return mediasInRoi.includes(row.variable);
        });

        return groupContributionData({
            rows: rowsInRoi,
            variableColorMap,
            categoryColorMap,
            mediaOnly: false,
            variableNameMap,
        });
    }, [
        previous?.roiCurves.variablesAndCategories.byVariable,
        contributionData?.rows,
        variableColorMap,
        categoryColorMap,
        variableNameMap,
    ]);

    const structuredSeasonalityData = useMemo(() => {
        return structureMediaSpendSeasonality(totalContributionGraphPeriod, {
            variableColorMap,
            categoryColorMap,
            variableNameMap,
            optimizedData: optimized,
            totalKpi: optimized?.totalKpi || 0,
            variablesAndCategories: previous?.roiCurves
                .variablesAndCategories || {
                byCategory: {},
                byVariable: {},
            },
        });
    }, [
        variableNameMap,
        totalContributionGraphPeriod,
        variableColorMap,
        categoryColorMap,
        optimized,
        previous?.roiCurves.variablesAndCategories,
    ]);

    const historicTotalContributionByDate = useMemo(() => {
        const byDate: Record<string, number> = {};
        contributionData?.rows?.forEach(({ periodName: date, contribution }) => {
            byDate[date] = (byDate[date] || 0) + contribution;
        });
        return byDate;
    }, [contributionData?.rows]);

    const seasonalityData: {
        previous: ContributionSeasonalityData;
        optimized: ContributionSeasonalityData;
    } = useMemo(() => {
        return {
            previous: {
                data:
                    modelBreakdown === 'category'
                        ? modelContributionData.dataByCategory
                        : modelContributionData.dataByVariable,
                dates: modelContributionData.dates,
                totalContributionByDate: historicTotalContributionByDate,
            },
            optimized: {
                data:
                    modelBreakdown === 'category'
                        ? structuredSeasonalityData.dataByCategory
                        : structuredSeasonalityData.dataByVariable,
                dates: structuredSeasonalityData.dates,
                totalContributionByDate:
                    structuredSeasonalityData.totalContributionByDate,
            },
        };
    }, [
        modelBreakdown,
        modelContributionData.dataByCategory,
        modelContributionData.dataByVariable,
        modelContributionData.dates,
        historicTotalContributionByDate,
        structuredSeasonalityData.dataByCategory,
        structuredSeasonalityData.dataByVariable,
        structuredSeasonalityData.dates,
        structuredSeasonalityData.totalContributionByDate,
    ]);

    return (
        <div className="relative">
            <WithLoader isLoading={isLoading} isFetching={isFetching}>
                <div className="flex flex-wrap items-center gap-2 mb-4">
                    <Typography
                        variant="titleS"
                        tooltip="Check the total recommended media spending in each period, as well as the predicted KPI generated in that period."
                    >
                        Total Contribution vs Media Investments
                    </Typography>
                    <TabButtons
                        aria-label="Periodicity"
                        variant="pills"
                        className="gap-2 mx-2"
                        onActiveTabChange={(activeTab) => {
                            setTotalContributionGraphPeriod(
                                activeTab === 0 ? 'weekly' : 'monthly',
                            );
                        }}
                        activeTab={selectedIndex}
                    >
                        {['weekly', 'monthly'].map((periodName) => {
                            return (
                                <TabButtons.Item
                                    key={periodName}
                                    title={capitalizeFirstLetter(periodName)}
                                />
                            );
                        })}
                    </TabButtons>
                    <Select
                        className="min-w-48"
                        selectedValue={modelBreakdown}
                        onSelectChange={(value) => {
                            setModelBreakdown(value);
                        }}
                        dismissOnClick
                        label="Select..."
                        compact
                    >
                        <Select.Option label={'By Category'} value={'category'} />
                        <Select.Option label={'By Variable'} value={'variable'} />
                    </Select>
                    <span className="grow" />
                    {children ? <div className="ml-2">{children}</div> : null}
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-10 gap-5 xl:h-128 my-6">
                    <div className="col-span-10 xl:col-span-7 h-96 xl:h-full">
                        <ContributionSeasonalityBarChart
                            className="h-full"
                            isLoading={false}
                            data={seasonalityData}
                            period={totalContributionGraphPeriod}
                        />
                    </div>
                    <div className="col-span-10 xl:col-span-3">
                        <OptimizedPieChart
                            data={budgetByCategoryData.simulated}
                            showPercent={false}
                        />
                    </div>
                </div>
            </WithLoader>
        </div>
    );
};
